import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Separator, FlexView } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';

import {
  useGetAgentDetails,
  useGetAgents,
  useGetTicketDetail,
  invalidateTicketDetail,
  usePatchTicket,
  invalidateTickets,
} from 'actions/support-agent';
import { showToast } from 'actions/toast-msg';

import { initPubnub, getPubnub, subscribe, teardown } from './pubnub-manager';
import { playNotificationSound, unlockAudioContext } from './notification-sound';
import { getCookie } from 'utils';
import {
  Workspace,
  ModalOverlay,
  ModalCard,
  ModalHead,
  ModalBody,
  ModalFoot,
  BtnPrimary,
  BtnSecondary,
  AgentItem,
  AgentAvatar,
  IconBtn,
  TransferSection,
  TransferLabel,
  AgentInfo,
  AgentName,
  AgentSpecialty,
  StatusDot,
  OfflineAvatar,
  ModalErrorMsg,
} from './styled-comps';
import LeftPanel from './components/left-panel';
import Thread from './components/thread';
import ContextPanel from './components/context-panel';
import TransferModal from './components/transfer-modal';
import EmailModal from './components/email-modal';
import Icon from './components/icon';
import { ERROR_AUTO_CLEAR_MS } from './constants';

// ── Empty state when no ticket selected ────────────────────────────────
const EmptyMain = styled(FlexView)`
  && {
    grid-column: 2 / -1;
    align-items: center;
    justify-content: center;
    gap: 12px;
    background: #faf8f5;
  }
`;

const EmptyTitle = styled(Text)`
  && {
    font-size: 16px;
    font-weight: 600;
    color: #6b6560;
  }
`;

const EmptySub = styled(Text)`
  && {
    font-size: 13px;
    color: #9a938b;
  }
`;

const SupportAgent = () => {
  const dispatch = useDispatch();
  const [activeTicketId, setActiveTicketId] = useState(null);
  const [readTicketIds, setReadTicketIds] = useState(new Set());
  const [transferOpen, setTransferOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedAssignAgent, setSelectedAssignAgent] = useState(null);
  const [assignError, setAssignError] = useState(null);

  // --- API hooks ---
  const { data: agentDetails, get: getAgentDetails } = useGetAgentDetails();
  const {
    data: ticketDetailData,
    get: getTicketDetail,
    isStale: ticketDetailStale,
    isProcessing: ticketDetailProcessing,
  } = useGetTicketDetail();
  const latestTicket = ticketDetailData?.ticket || null;
  const activeTicketRef = useRef(null);
  if (latestTicket) activeTicketRef.current = latestTicket;
  const activeTicket = latestTicket || activeTicketRef.current;

  // Thread registers its optimistic-append fn here so the compose-email modal
  // can show a sent standalone email immediately.
  const appendExternalRef = useRef(null);

  useEffect(() => {
    if (activeTicketId) {
      activeTicketRef.current = null;
      getTicketDetail(activeTicketId);
    }
  }, [activeTicketId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (ticketDetailStale && !ticketDetailProcessing && activeTicketId) {
      getTicketDetail(activeTicketId);
    }
  }, [ticketDetailStale, ticketDetailProcessing]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    data: agentsData,
    get: getAgents,
    isProcessingFailed: agentsFailed,
  } = useGetAgents();
  const { patch: patchTicket, isProcessing: assignProcessing } = usePatchTicket({
    onComplete: error => {
      if (!error && assignOpen) {
        setAssignOpen(false);
        setSelectedAssignAgent(null);
        setAssignError(null);
        invalidateTicketDetail(activeTicketId);
        invalidateTickets();
      } else if (error) {
        setAssignError(typeof error === 'string' ? error : 'Failed to assign ticket.');
      }
    },
  });

  const userRole = agentDetails?.role || agentDetails?.agent?.role || 'agent';
  const agentProfile = agentDetails?.agent || agentDetails;

  // Mirror activeTicket.id into a ref so the PubNub listener (whose effect
  // depends only on agentProfile.id) can read the current value without
  // capturing a stale closure.
  const activeTicketIdRef = useRef(null);
  useEffect(() => {
    activeTicketIdRef.current = activeTicket?.id ?? null;
  }, [activeTicket?.id]);

  const agents = useMemo(() => agentsData?.agents || [], [agentsData]);

  const onlineAgents = useMemo(() => agents.filter(a => a.is_online || a.online), [
    agents,
  ]);
  const offlineAgents = useMemo(() => agents.filter(a => !a.is_online && !a.online), [
    agents,
  ]);

  useEffect(() => {
    const userId = getCookie('user_id') || getCookie('emp_id');
    if (userId) getAgentDetails(userId);
  }, [getAgentDetails]);

  // Unlock the notification AudioContext on first gesture; re-check on
  // tab-visible to recover from Chrome's background-tab re-suspend.
  useEffect(() => {
    const unlock = () => unlockAudioContext();
    const onVisible = () => {
      if (!document.hidden) unlockAudioContext();
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // --- PubNub: init, subscribe, and dashboard listener ---
  useEffect(() => {
    const userId = agentProfile?.user_id;
    if (!userId) return;

    const pubnub = initPubnub(userId);
    if (!pubnub) return;

    subscribe(agentProfile.common_channel_ids);

    const dashboardChannels = new Set(agentProfile.common_channel_ids || []);

    const listener = {
      message: event => {
        if (!dashboardChannels.has(event.channel)) return;
        const evt = event.message;
        if (
          evt.event_type === 'TICKET_ASSIGNED' ||
          evt.event_type === 'TICKET_TRANSFERRED'
        ) {
          invalidateTickets();
          // Backend publishes TICKET_ASSIGNED only on the new assignee's
          // support_agent.<agent_id> channel, so the dashboardChannels gate
          // above already ensures this is for the current logged-in agent.
          if (evt.event_type === 'TICKET_ASSIGNED') {
            playNotificationSound();
            const ticketRef = evt.ticket_id ? evt.ticket_id.slice(0, 8) : '';
            const detail = evt.preview || (ticketRef && `#${ticketRef}`) || '';
            const message = detail
              ? `New chat assigned: ${detail}`
              : 'New chat assigned to you';
            dispatch(showToast(message, null, 5000));
          }
        }
        if (evt.event_type === 'NEW_MESSAGE') {
          playNotificationSound();
          if (evt.ticket_id && evt.ticket_id === activeTicketIdRef.current) {
            // Customer replies on a resolved ticket auto-reopen it on the
            // backend; the only signal is this NEW_MESSAGE with the new
            // `ticket_status`. Refresh the detail so the header & resolve
            // button reflect the new state.
            invalidateTicketDetail(evt.ticket_id);
            invalidateTickets();
          } else if (evt.ticket_id) {
            setReadTicketIds(prev => {
              const next = new Set(prev);
              next.delete(evt.ticket_id);
              return next;
            });
            invalidateTickets();
          }
        }
        if (
          evt.event_type === 'TICKET_RESOLVED' ||
          evt.event_type === 'TICKET_REOPENED' ||
          evt.event_type === 'TICKET_CLOSED'
        ) {
          if (evt.ticket_id && evt.ticket_id === activeTicketIdRef.current) {
            invalidateTicketDetail(evt.ticket_id);
          }
          invalidateTickets();
        }
      },
    };
    pubnub.addListener(listener);

    return () => {
      pubnub.removeListener(listener);
      teardown();
    };
  }, [agentProfile?.user_id, agentProfile?.common_channel_ids]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Fetch agents when assign modal opens ---
  useEffect(() => {
    if (assignOpen) {
      getAgents('agents');
      setAssignError(null);
    }
  }, [assignOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- PubNub: subscribe to active ticket channels ---
  useEffect(() => {
    const ticketChannel = activeTicket?.pubnub_channel;
    if (!ticketChannel) return;
    const internalChannel = ticketChannel.replace('support.', 'support_agent_internal.');
    subscribe([ticketChannel, internalChannel]);
    return () => {
      const pb = getPubnub();
      if (pb) pb.unsubscribe({ channels: [ticketChannel, internalChannel] });
    };
  }, [activeTicket?.pubnub_channel]);

  // Auto-clear assign error
  useEffect(() => {
    if (!assignError) return;
    const timer = setTimeout(() => setAssignError(null), ERROR_AUTO_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [assignError]);

  const handlePickTicket = useCallback(ticket => {
    setActiveTicketId(ticket.id);
  }, []);

  const handleAssign = useCallback(() => {
    if (selectedAssignAgent && activeTicket?.id) {
      patchTicket(activeTicket.id, {
        action: 'update_owner',
        owner_id: selectedAssignAgent.id,
        owner_type: 'agent',
      });
    }
  }, [selectedAssignAgent, activeTicket, patchTicket]);

  const handleTicketUpdated = useCallback(() => {
    invalidateTicketDetail(activeTicketId);
    invalidateTickets();
  }, [activeTicketId]);

  return (
    <Workspace>
      <LeftPanel
        activeTicketId={activeTicketId}
        onPickTicket={handlePickTicket}
        userRole={userRole}
        isOnline={Boolean(agentProfile?.is_online)}
        readTicketIds={readTicketIds}
      />
      {activeTicketId ? (
        <>
          <Thread
            activeTicket={activeTicket}
            agentMe={agentProfile}
            userRole={userRole}
            onMarkRead={id => setReadTicketIds(prev => new Set([...prev, id]))}
            onAssign={() => setAssignOpen(true)}
            onTicketUpdated={handleTicketUpdated}
            onPickTicket={handlePickTicket}
            bindExternalAppend={fn => {
              appendExternalRef.current = fn;
            }}
          />
          <ContextPanel
            activeTicket={activeTicket}
            onTransfer={() => setTransferOpen(true)}
            onSendEmail={() => setEmailModalOpen(true)}
            onTicketUpdated={handleTicketUpdated}
            onPickTicket={handlePickTicket}
            userRole={userRole}
          />
        </>
      ) : (
        <EmptyMain>
          <Icon name="chat" size={48} color="#d6cfc6" />
          <EmptyTitle>No ticket selected</EmptyTitle>
          <EmptySub>Select a ticket from the list to view the conversation</EmptySub>
        </EmptyMain>
      )}

      <TransferModal
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        ticketId={activeTicket?.id}
        currentTeamId={activeTicket?.team_id || null}
        onTicketUpdated={handleTicketUpdated}
      />

      <EmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        ticketId={activeTicket?.id}
        onTicketUpdated={handleTicketUpdated}
        onSent={item => appendExternalRef.current?.(item)}
      />

      {/* Assign modal — for leads assigning unassigned tickets */}
      {assignOpen && (
        <ModalOverlay onClick={() => setAssignOpen(false)}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalHead>
              <Text variant="body1Medium">Assign ticket to agent</Text>
              <IconBtn onClick={() => setAssignOpen(false)}>
                <Icon name="close" size={16} />
              </IconBtn>
            </ModalHead>
            <ModalBody>
              {agentsFailed && (
                <ModalErrorMsg>Failed to load agents. Please try again.</ModalErrorMsg>
              )}
              {assignError && <ModalErrorMsg>{assignError}</ModalErrorMsg>}
              <TransferSection>
                <TransferLabel>Online agents ({onlineAgents.length})</TransferLabel>
                {onlineAgents.map(agent => (
                  <AgentItem
                    key={agent.id}
                    $selected={selectedAssignAgent?.id === agent.id}
                    onClick={() => setSelectedAssignAgent(agent)}
                  >
                    <AgentAvatar>
                      {(agent.name || '').slice(0, 2).toUpperCase()}
                    </AgentAvatar>
                    <AgentInfo>
                      <AgentName>{agent.name}</AgentName>
                      <AgentSpecialty>
                        {agent.specialty ||
                          `${agent.open_ticket_count ||
                            agent.open_count ||
                            0} open tickets`}
                      </AgentSpecialty>
                    </AgentInfo>
                    <StatusDot $online />
                  </AgentItem>
                ))}
              </TransferSection>
              {offlineAgents.length > 0 && (
                <>
                  <Separator height={4} transparent />
                  <TransferSection>
                    <TransferLabel>Offline agents ({offlineAgents.length})</TransferLabel>
                    {offlineAgents.map(agent => (
                      <AgentItem
                        key={agent.id}
                        $selected={selectedAssignAgent?.id === agent.id}
                        onClick={() => setSelectedAssignAgent(agent)}
                      >
                        <OfflineAvatar>
                          {(agent.name || '').slice(0, 2).toUpperCase()}
                        </OfflineAvatar>
                        <AgentInfo>
                          <AgentName>{agent.name}</AgentName>
                          <AgentSpecialty>
                            {agent.specialty ||
                              `${agent.open_ticket_count ||
                                agent.open_count ||
                                0} open tickets`}
                          </AgentSpecialty>
                        </AgentInfo>
                        <StatusDot $online={false} />
                      </AgentItem>
                    ))}
                  </TransferSection>
                </>
              )}
            </ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={() => setAssignOpen(false)}>Cancel</BtnSecondary>
              <BtnPrimary
                onClick={handleAssign}
                disabled={!selectedAssignAgent || assignProcessing}
              >
                {assignProcessing ? 'Assigning...' : 'Assign ticket'}
              </BtnPrimary>
            </ModalFoot>
          </ModalCard>
        </ModalOverlay>
      )}
    </Workspace>
  );
};

export default SupportAgent;
